import { z } from 'zod';
import { registry } from '../registry';
export const labDrawCardTitles = {
    root: 'Lab Draw',
    labSpecimen: 'Lab specimen collection',
    bloodDraw: 'Blood draw (venipuncture / capillary)',
    urineSpecimen: 'Urine specimen',
    woundCulture: 'Wound culture',
    specimenCollection: 'Sputum / stool / other specimen',
    resultsCommunication: 'Results and communication',
    venipunctureProcedure: 'Venipuncture Procedure',
    patientResponse: 'Patient Response and Specimen Integrity',
};
const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
export const baseLabDrawSchema = z
    .object({
    labSpecimenPresent: z.boolean().optional().register(registry, { label: 'Was a lab specimen collected this visit?' }),
    bloodDrawPresent: z.boolean().optional().register(registry, { label: 'Was a blood draw performed this visit?' }),
    urineSpecimenPresent: z.boolean().optional().register(registry, { label: 'Was a urine specimen collected this visit?' }),
    woundCulturePresent: z.boolean().optional().register(registry, { label: 'Was a wound culture obtained this visit?' }),
    specimenCollectionPresent: z.boolean().optional().register(registry, { label: 'Was a specimen collected this visit?' }),
    resultsCommunicationPresent: z
        .boolean()
        .optional()
        .register(registry, { label: 'Were lab results reviewed or communicated this visit?' }),
    orderingPhysician: z.string().optional().register(registry, { label: 'Ordering Physician / NP' }),
    labOrderDate: z.string().optional().register(registry, { label: 'Lab Order Date' }),
    requisitionNumber: z.string().optional().register(registry, { label: 'Requisition / Order Number' }),
    labReceiving: z
        .string()
        .optional()
        .register(registry, {
        label: 'Lab Receiving Specimen',
        options: [
            { value: 'labcorp', label: 'LabCorp' },
            { value: 'quest', label: 'Quest Diagnostics' },
            { value: 'hospital', label: 'Hospital-based Lab' },
            { value: 'physician_office', label: 'Physician Office Lab' },
            { value: 'agency_poc', label: 'Agency In-House POC' },
            { value: 'other', label: 'Other' },
        ],
    }),
    specimenPickup: z
        .string()
        .optional()
        .register(registry, {
        label: 'Specimen Pickup / Courier',
        options: [
            { value: 'courier', label: 'Courier pickup — time arranged' },
            { value: 'nurse', label: 'Nurse transported to lab' },
            { value: 'patient', label: 'Patient/caregiver transported' },
            { value: 'poc', label: 'POC — processed on site' },
        ],
    }),
    pickupTimeAndCourier: z.string().optional().register(registry, { label: 'Specimen Pickup Time / Courier Name' }),
    resultsRecipients: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Who Receives Results',
        options: [
            { value: 'agency_supervisor', label: 'Agency clinical supervisor — review and routes' },
            { value: 'ordering_physician', label: 'Ordering physician — direct fax per order' },
            { value: 'patient_notified', label: 'Patient notified of results by RN' },
            { value: 'caregiver_notified', label: 'Caregiver notified — patient consent on file' },
        ],
    }),
    resultsExpectedTurnaround: z
        .string()
        .optional()
        .register(registry, {
        label: 'Expected Turnaround',
        options: [
            { value: 'routine', label: 'Routine — 24–48 hours' },
            { value: 'stat', label: 'STAT — same day' },
            { value: 'culture', label: 'Culture — 48–72 hours' },
            { value: 'sensitivity', label: 'Sensitivity — 5–7 days' },
        ],
    }),
    resultsPriorReviewed: z
        .string()
        .optional()
        .register(registry, {
        label: 'Prior Lab Results Reviewed With Patient This Visit?',
        options: [
            { value: 'yes', label: 'Yes — reviewed' },
            { value: 'no', label: 'No' },
            { value: 'pending', label: 'Pending — results not yet available' },
        ],
    }),
    resultsClinicalNarrative: z.string().optional().register(registry, { label: 'Clinical Narrative — Lab Section' }),
    woundCultureTypes: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Culture Type Ordered',
        options: [
            { value: 'swab_aerobic', label: 'Wound swab — aerobic' },
            { value: 'swab_aerobic_anaerobic', label: 'Wound swab — aerobic + anaerobic' },
            { value: 'tissue_biopsy', label: 'Tissue biopsy — deep culture' },
            { value: 'fluid_aspiration', label: 'Fluid aspiration' },
            { value: 'mrsa_screen', label: 'MRSA screen swab' },
        ],
    }),
    woundSiteCultured: z.string().optional().register(registry, { label: 'Wound Site Cultured' }),
    woundBedAppearance: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Wound Bed Appearance at Time of Culture',
        options: [
            { value: 'granulation', label: 'Granulation tissue present' },
            { value: 'slough', label: 'Slough present' },
            { value: 'eschar', label: 'Eschar present' },
            { value: 'purulent', label: 'Purulent exudate' },
            { value: 'cellulitis', label: 'Signs of cellulitis / spreading erythema' },
            { value: 'malodorous', label: 'Malodorous / necrotic' },
        ],
    }),
    woundCultureReason: z
        .string()
        .optional()
        .register(registry, {
        label: 'Reason for Culture',
        options: [
            { value: 'infection_signs', label: 'Clinical signs of infection' },
            { value: 'not_progressing', label: 'Wound not progressing per protocol' },
            { value: 'exudate_change', label: 'Change in exudate / odor' },
            { value: 'routine_surveillance', label: 'Routine surveillance per MD order' },
            { value: 'pre_operative', label: 'Pre-operative / pre-procedure' },
        ],
    }),
    urineTestsOrdered: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Test Ordered',
        options: [
            { value: 'ua', label: 'Urinalysis (UA)' },
            { value: 'cs', label: 'Culture and sensitivity (C&S)' },
            { value: 'ua_reflex_cs', label: 'UA with reflex to C&S' },
            { value: 'drug_screen', label: 'Urine drug screen' },
            { value: '24hr', label: '24-hour urine collection' },
            { value: 'protein_creatinine', label: 'Urine protein / creatinine ratio' },
            { value: 'cytology', label: 'Urine cytology' },
        ],
    }),
    urineCollectionMethod: z
        .string()
        .optional()
        .register(registry, {
        label: 'Collection Method',
        options: [
            { value: 'clean_catch_patient', label: 'Clean catch midstream — patient performed' },
            { value: 'clean_catch_assisted', label: 'Clean catch — nurse assisted' },
            { value: 'straight_cath', label: 'Straight catheter (in/out)' },
            { value: 'indwelling_port', label: 'Indwelling catheter — new port tap' },
            { value: 'indwelling_bag', label: 'Indwelling catheter — from bag (non-preferred)' },
            { value: 'suprapubic', label: 'Suprapubic catheter' },
        ],
    }),
    urineSpecimenColor: z
        .string()
        .optional()
        .register(registry, {
        label: 'Specimen Color / Appearance',
        options: [
            { value: 'clear', label: 'Clear' },
            { value: 'pale_yellow', label: 'Pale yellow' },
            { value: 'yellow', label: 'Yellow' },
            { value: 'dark_yellow', label: 'Dark yellow / amber' },
            { value: 'orange', label: 'Orange' },
            { value: 'pink_red', label: 'Pink / red (blood)' },
            { value: 'brown', label: 'Brown / tea-colored' },
            { value: 'cloudy', label: 'Cloudy / turbid' },
            { value: 'milky', label: 'Milky / white' },
        ],
    }),
    urineOdor: z
        .string()
        .optional()
        .register(registry, {
        label: 'Odor',
        options: [
            { value: 'normal', label: 'Normal / mild' },
            { value: 'malodorous', label: 'Malodorous / foul' },
            { value: 'ammonia', label: 'Ammonia-like' },
            { value: 'fruity', label: 'Fruity (ketones)' },
        ],
    }),
    urineVolumeAdequacy: z
        .string()
        .optional()
        .register(registry, {
        label: 'Volume / Adequacy',
        options: [
            { value: 'adequate', label: 'Adequate volume — submitted' },
            { value: 'small', label: 'Small volume — lab notified' },
            { value: 'unable', label: 'Unable to collect — patient unable to void' },
        ],
    }),
    urineDipstickResults: z
        .string()
        .optional()
        .register(registry, {
        label: 'Dipstick / POC UA Results (if performed)',
        options: [
            { value: 'not_performed', label: 'Not performed — sent to lab' },
            { value: 'negative', label: 'Negative — normal' },
            { value: 'leukocytes_nitrites', label: 'Positive leukocytes / nitrites' },
            { value: 'blood_protein', label: 'Positive blood / protein' },
            { value: 'glucose', label: 'Glucose elevated' },
            { value: 'multiple_abnormals', label: 'Multiple abnormals — MD notified' },
        ],
    }),
    urinePatientSymptoms: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Patient Symptoms at Collection',
        options: [
            { value: 'asymptomatic', label: 'Asymptomatic' },
            { value: 'dysuria', label: 'Dysuria / burning' },
            { value: 'frequency', label: 'Frequency / urgency' },
            { value: 'suprapubic_pain', label: 'Suprapubic pain' },
            { value: 'flank_pain', label: 'Flank pain / CVA tenderness' },
            { value: 'fever', label: 'Fever — MD notified' },
            { value: 'mental_status', label: 'Change in mental status (elderly UTI)' },
        ],
    }),
    urineDisposal: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Disposal per Infection Control',
        options: [{ value: 'biohazard', label: 'Biohazard disposal per agency policy — confirmed' }],
    }),
    specimenTypes: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Specimen Type',
        options: [
            { value: 'sputum_culture', label: 'Sputum — culture and sensitivity' },
            { value: 'sputum_afb', label: 'Sputum — AFB (TB)' },
            { value: 'stool_cdiff', label: 'Stool — C. diff toxin' },
            { value: 'stool_op', label: 'Stool — O&P (ova and parasites)' },
            { value: 'stool_fobt', label: 'Stool — occult blood (FOBT)' },
            { value: 'nasal_swab', label: 'Nasal swab — influenza / COVID / RSV' },
            { value: 'throat_swab', label: 'Throat swab — group A strep' },
            { value: 'other', label: 'Other — specify below' },
        ],
    }),
    specimenCustomType: z.string().optional().register(registry, { label: 'Other — Specify' }),
    specimenCollectionNotes: z.string().optional().register(registry, { label: 'Collection Notes' }),
    bloodDrawTestsOrdered: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Tests Ordered',
        options: [
            { value: 'cbc_diff', label: 'CBC with differential' },
            { value: 'cmp', label: 'CMP (comprehensive metabolic)' },
            { value: 'bmp', label: 'BMP (basic metabolic)' },
            { value: 'pt_inr', label: 'PT/INR' },
            { value: 'ptt_aptt', label: 'PTT/aPTT' },
            { value: 'bnp', label: 'BNP / NT-proBNP' },
            { value: 'hba1c', label: 'HbA1c' },
            { value: 'lipid', label: 'Lipid panel' },
            { value: 'thyroid', label: 'Thyroid (TSH / T4)' },
            { value: 'digoxin', label: 'Digoxin level' },
            { value: 'vancomycin', label: 'Vancomycin trough' },
            { value: 'drug_level_other', label: 'Drug level (other)' },
            { value: 'blood_culture', label: 'Blood culture x2' },
            { value: 'type_screen', label: 'Type and screen' },
            { value: 'other', label: 'Other — specify below' },
        ],
    }),
    bloodDrawCustomTest: z.string().optional().register(registry, { label: 'Other Test — Specify' }),
    bloodDrawTubesCollected: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Tubes Collected',
        options: [
            { value: 'red_sst', label: 'Red / SST' },
            { value: 'lavender_edta', label: 'Lavender / EDTA' },
            { value: 'blue_citrate', label: 'Blue / citrate' },
            { value: 'green_heparin', label: 'Green / heparin' },
            { value: 'yellow_culture', label: 'Yellow / culture' },
            { value: 'orange_thrombin', label: 'Orange / thrombin' },
            { value: 'pearl_ppt', label: 'Pearl / PPT' },
            { value: 'pink_blood_bank', label: 'Pink / blood bank' },
        ],
    }),
    bloodDrawSiteAccessed: z
        .string()
        .optional()
        .register(registry, {
        label: 'Site Accessed',
        options: [
            { value: 'antecubital_right', label: 'Antecubital — right' },
            { value: 'antecubital_left', label: 'Antecubital — left' },
            { value: 'forearm_right', label: 'Forearm — right' },
            { value: 'forearm_left', label: 'Forearm — left' },
            { value: 'hand_right', label: 'Hand — right' },
            { value: 'hand_left', label: 'Hand — left' },
            { value: 'wrist_right', label: 'Wrist — right' },
            { value: 'wrist_left', label: 'Wrist — left' },
            { value: 'central_line_picc', label: 'Central line / PICC' },
            { value: 'port_a_cath', label: 'Port-a-cath' },
            { value: 'capillary_fingerstick', label: 'Capillary — fingerstick' },
            { value: 'capillary_heelstick', label: 'Capillary — heelstick' },
        ],
    }),
    bloodDrawNeedleGauge: z
        .string()
        .optional()
        .register(registry, {
        label: 'Needle Gauge',
        options: [
            { value: '18g', label: '18G' },
            { value: '21g_butterfly', label: '21G (butterfly)' },
            { value: '21g_straight', label: '21G (straight)' },
            { value: '22g_butterfly', label: '22G (butterfly)' },
            { value: '23g', label: '23G' },
            { value: '25g_butterfly', label: '25G (butterfly)' },
        ],
    }),
    bloodDrawAttempts: z
        .string()
        .optional()
        .register(registry, {
        label: 'Number of Attempts',
        options: [
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
        ],
    }),
    bloodDrawDifficultAccess: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Difficult Access — Reason (if applicable)',
        options: [
            { value: 'small_rolling', label: 'Small / rolling veins' },
            { value: 'dehydration', label: 'Dehydration' },
            { value: 'obesity', label: 'Obesity / subcutaneous tissue' },
            { value: 'scarring', label: 'Scarring / prior IV history' },
            { value: 'mastectomy_right', label: 'Mastectomy — right arm avoid' },
            { value: 'mastectomy_left', label: 'Mastectomy — left arm avoid' },
            { value: 'lymphedema', label: 'Lymphedema — arm avoid' },
            { value: 'picc_port', label: 'PICC / port required' },
        ],
    }),
    bloodDrawPatientTolerance: z
        .string()
        .optional()
        .register(registry, {
        label: 'Patient Tolerance',
        options: [
            { value: 'well', label: 'Tolerated well — no complaints' },
            { value: 'mild', label: 'Tolerated — mild discomfort verbalized' },
            { value: 'poorly', label: 'Tolerated poorly — vasovagal / dizziness' },
            { value: 'refused', label: 'Refused / unable to complete' },
        ],
    }),
    bloodDrawSiteCondition: z
        .string()
        .optional()
        .register(registry, {
        label: 'Site Condition Post-Draw',
        options: [
            { value: 'hemostasis', label: 'Hemostasis achieved — bandage applied' },
            { value: 'prolonged_bleeding', label: 'Prolonged bleeding — pressure applied' },
            { value: 'hematoma', label: 'Hematoma noted — cold compress applied' },
            { value: 'nerve_strike', label: 'Nerve strike — pain radiating — MD notified' },
        ],
    }),
    bloodDrawSpecimenIntegrity: z
        .string()
        .optional()
        .register(registry, {
        label: 'Specimen Integrity',
        options: [
            { value: 'adequate', label: 'Adequate — tubes filled appropriately' },
            { value: 'hemolysis', label: 'Hemolysis suspected — lab notified' },
            { value: 'insufficient', label: 'Insufficient volume — recollect ordered' },
            { value: 'clotted', label: 'Clotted — recollect ordered' },
        ],
    }),
    bloodDrawSharpsDisposal: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Sharps and Waste Disposal',
        options: [
            {
                value: 'agency_container',
                label: 'Sharps disposed in portable sharps container per agency P&P',
            },
            {
                value: 'left_with_patient',
                label: 'Sharps container left with patient — label applied',
            },
        ],
    }),
    bloodDrawSpecimenLabeling: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Specimen Labeling Confirmed',
        options: [
            {
                value: 'bedside_labels',
                label: 'Labels applied at bedside — patient name, DOB, MRN, date/time, nurse initials',
            },
            {
                value: 'printed_labels',
                label: 'Labels printed from requisition — verified against patient ID',
            },
        ],
    }),
})
    .register(registry, { label: labDrawCardTitles.root });
/** Display-only yes/no options for present-gate SegmentedControls (boolean fields). */
export const labDrawPresentYesNoOptions = yesNoOptions;
